import { CloseIcon, PlusIcon } from 'components/icons'
import { useEffect, useRef, useState } from 'react'
import Button from '../button'
import { TextInput } from '../input'
import { FormSetValue } from 'types/input'
import { FieldErrors, FieldValues, Path, PathValue } from 'react-hook-form'

type Props<T extends FieldValues> = {
  label: string
  placeholder: string
  defaultValues?: string[]
  errors?: FieldErrors<T>
  className?: string
  orientation?: 'horizontal' | 'vertical'
  shouldDelete?: boolean
} & FormSetValue<T>

/**
 * DynamicInputList component
 *
 * This component will render a list of inputs and
 * will allow the user to add or remove the input from the list.
 */
const DynamicInputList = <T extends FieldValues>({
  label,
  placeholder,
  setValue,
  name,
  defaultValues,
  errors,
  className,
  orientation = 'vertical',
  shouldDelete = true,
}: Props<T>) => {
  const [inputList, setInputList] = useState<string[]>([])
  const [duplicateError, setDuplicateError] = useState<string>('')

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (defaultValues) {
      setInputList(defaultValues)
    }
  }, [defaultValues])

  const submitInput = () => {
    const input = inputRef?.current?.value?.trim()
    if (!input) return

    if (inputList.includes(input)) {
      setDuplicateError('Same input not valid')
      return
    }

    const value = [...inputList, input]
    setInputList(value)
    inputRef.current!.value = ''
    setValue(name, value as PathValue<T, string & Path<T>>, {
      shouldValidate: true,
    })
    setDuplicateError('')
  }

  const deleteInput = (index: number) => {
    const value = inputList
    value.splice(index, 1)
    setInputList([...value])
    setValue(name, value as PathValue<T, string & Path<T>>)
    setDuplicateError('')
  }

  return (
    <div>
      <div
        className={`flex w-full items-start ${
          orientation === 'horizontal' ? 'flex-row gap-6' : 'flex-col gap-3 '
        } ${className}`}
      >
        <div
          className={`flex ${
            orientation === 'horizontal' ? 'min-w-[350px]' : 'w-full'
          }  shrink-0 items-end gap-x-3 `}
        >
          <TextInput ref={inputRef} label={label} placeholder={placeholder} />
          <Button
            type="button"
            size="large"
            variant="outline"
            className="size-[44px] !border"
            icon={<PlusIcon />}
            onClick={submitInput}
          />
        </div>

        {inputList.length > 0 && (
          <div className="flex h-full max-h-[250px] w-full flex-col divide-y overflow-y-auto rounded-md border">
            {inputList.map((list, i) => (
              <div
                key={list + i}
                className="flex items-start justify-between gap-x-2 px-3 py-2.5 hover:bg-gray-50"
              >
                <p className="text-sm text-gray-700">{list}</p>
                {shouldDelete && (
                  <Button
                    type="button"
                    variant="custom"
                    className="mt-1"
                    size="small"
                    iconClassName="!size-3 text-gray-400 hover:text-red-400"
                    icon={<CloseIcon />}
                    onClick={() => deleteInput(i)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Error display */}

      <p className="mt-3 text-xs text-red-600">
        {duplicateError || (errors?.[name]?.message as string) || ''}
      </p>
    </div>
  )
}

export default DynamicInputList
